// 📄 components/Configuracion/ConfiguracionCig.tsx
// Configuración de conexión al Web Service CIG (Estatus de Facturas):
// Data Center, Tipo de ERP, System ID por ambiente y credenciales del
// PUser (opcionalmente guardadas). Las credenciales nunca se leen/escriben
// directo a Firestore — todo pasa por onehubApi (clienteConexionService.ts).
import { useEffect, useState } from "react";
import { RefreshCw, KeyRound, Save, ShieldCheck, Fingerprint } from "lucide-react";
import { useAuth } from "../../context/AuthContext.tsx";
import {
  obtenerConfigCliente,
  guardarConfigCliente,
  obtenerEstadoCodigoProduccion,
  guardarCodigoProduccion,
  guardarAnidsCliente,
  CigConexionConfig,
  SystemIds,
  SystemIdTestPorDefecto
} from "../../services/clienteConexionService.ts";
import FlujoAprobacionCredencial from "../credenciales/FlujoAprobacionCredencial.tsx";

const SYSTEM_IDS_VACIOS: SystemIds = { dev: "", qas: "", prd: "" };

// Mismo formato que valida el backend (functions/services/anidService.js).
const RE_ANID_BASE = /^AN\d{6,}$/;
const normalizarAnid = (valor: string) => valor.trim().toUpperCase().replace(/-T$/, "");

export default function ConfiguracionCig({ clienteId }: { clienteId: string }) {
  // Los ANIDs solo los edita un admin de plataforma (el backend lo exige):
  // con ellos la extensión de Chrome resuelve a qué cliente/CIG va un
  // estatus de factura.
  const { profile } = useAuth();
  const esAdmin = profile?.role === "admin";
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [dataCenter, setDataCenter] = useState("");
  const [erpType, setErpType] = useState<"onprem" | "cloud" | "">("");
  const [systemIds, setSystemIds] = useState<SystemIds>(SYSTEM_IDS_VACIOS);
  const [systemIdTestPorDefecto, setSystemIdTestPorDefecto] = useState<SystemIdTestPorDefecto>("");
  const [buyerAnids, setBuyerAnids] = useState<string[]>([]);
  const [anidsTexto, setAnidsTexto] = useState("");
  const [guardandoAnids, setGuardandoAnids] = useState(false);
  const [errorAnids, setErrorAnids] = useState<string | null>(null);
  const [successAnids, setSuccessAnids] = useState<string | null>(null);
  const [tieneCredencialesGuardadas, setTieneCredencialesGuardadas] = useState(false);
  const [usuarioGuardado, setUsuarioGuardado] = useState("");
  const [usuario, setUsuario] = useState("");
  const [contraseña, setContraseña] = useState("");
  const [guardarCredenciales, setGuardarCredenciales] = useState(false);
  // Sube al guardar una contraseña nueva: el flujo de aprobación se recarga (cambia quién la guardó).
  const [versionCredencial, setVersionCredencial] = useState(0);

  const [tieneCodigoProduccion, setTieneCodigoProduccion] = useState(false);
  const [codigoProduccion, setCodigoProduccion] = useState("");
  const [confirmarCodigoProduccion, setConfirmarCodigoProduccion] = useState("");
  const [guardandoCodigo, setGuardandoCodigo] = useState(false);
  const [errorCodigo, setErrorCodigo] = useState<string | null>(null);
  const [successCodigo, setSuccessCodigo] = useState<string | null>(null);

  useEffect(() => {
    if (!clienteId) { setLoading(false); return; }
    setLoading(true);
    Promise.all([
      obtenerConfigCliente<CigConexionConfig>(clienteId, "cig"),
      obtenerEstadoCodigoProduccion(clienteId)
    ])
      .then(([config, codigoEstado]) => {
        setDataCenter(config.dataCenter);
        setErpType(config.erpType);
        setSystemIds(config.systemIds);
        setSystemIdTestPorDefecto(config.systemIdTestPorDefecto || "");
        setBuyerAnids(config.buyerAnids || []);
        setAnidsTexto((config.buyerAnids || []).join("\n"));
        setTieneCredencialesGuardadas(config.tieneCredencialesGuardadas);
        setUsuarioGuardado(config.usuario);
        setTieneCodigoProduccion(codigoEstado.tieneCodigoGuardado);
      })
      .catch((err) => {
        console.error("Error cargando configuración CIG:", err);
        setError("No se pudo cargar la configuración guardada.");
      })
      .finally(() => setLoading(false));
  }, [clienteId]);

  const handleGuardar = async () => {
    setError(null);
    setSuccess(null);

    if (guardarCredenciales && (!usuario.trim() || !contraseña)) {
      setError("Ingresa usuario y contraseña para guardar las credenciales.");
      return;
    }

    setGuardando(true);
    try {
      await guardarConfigCliente(clienteId, "cig", {
        dataCenter,
        erpType,
        systemIds,
        systemIdTestPorDefecto,
        usuario: guardarCredenciales ? usuario.trim() : undefined,
        contraseña: guardarCredenciales ? contraseña : undefined
      });

      if (guardarCredenciales) {
        setTieneCredencialesGuardadas(true);
        setUsuarioGuardado(usuario.trim());
        setUsuario("");
        setContraseña("");
        setGuardarCredenciales(false);
        setVersionCredencial((v) => v + 1);
      }
      setSuccess("Configuración guardada correctamente.");
    } catch (err: any) {
      setError(err.message || "Error al guardar la configuración.");
    } finally {
      setGuardando(false);
    }
  };

  const handleGuardarAnids = async () => {
    setErrorAnids(null);
    setSuccessAnids(null);
    const lista = Array.from(new Set(anidsTexto.split(/[\s,;]+/).map(normalizarAnid).filter(Boolean)));
    const invalidos = lista.filter((a) => !RE_ANID_BASE.test(a));
    if (invalidos.length) {
      setErrorAnids(`ANID inválido: ${invalidos.join(", ")}. Formato: AN seguido de números (ej. AN01404537294).`);
      return;
    }
    setGuardandoAnids(true);
    try {
      const guardados = await guardarAnidsCliente(clienteId, lista);
      setBuyerAnids(guardados);
      setAnidsTexto(guardados.join("\n"));
      setSuccessAnids(guardados.length ? "ANIDs guardados correctamente." : "Se quitaron los ANIDs de este cliente.");
    } catch (err: any) {
      setErrorAnids(err.message || "Error al guardar los ANIDs.");
    } finally {
      setGuardandoAnids(false);
    }
  };

  const handleGuardarCodigoProduccion = async () => {
    setErrorCodigo(null);
    setSuccessCodigo(null);

    if (!codigoProduccion.trim()) {
      setErrorCodigo("Ingresa el código de confirmación.");
      return;
    }
    if (codigoProduccion !== confirmarCodigoProduccion) {
      setErrorCodigo("Los dos campos deben coincidir.");
      return;
    }

    setGuardandoCodigo(true);
    try {
      await guardarCodigoProduccion(clienteId, codigoProduccion);
      setTieneCodigoProduccion(true);
      setCodigoProduccion("");
      setConfirmarCodigoProduccion("");
      setSuccessCodigo("Código de producción guardado correctamente.");
    } catch (err: any) {
      setErrorCodigo(err.message || "Error al guardar el código.");
    } finally {
      setGuardandoCodigo(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  if (loading) {
    return (
      <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <p className="text-sm">Cargando configuración...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
      )}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs">{success}</div>
      )}

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300">Conexión</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Tipo de ERP</label>
            <select className={inputClass} value={erpType} onChange={(e) => setErpType(e.target.value as "onprem" | "cloud" | "")}>
              <option value="">Selecciona una opción</option>
              <option value="onprem">On-Premise</option>
              <option value="cloud">S4HANA Cloud</option>
            </select>
          </div>
          <div>
            <label className={labelClass}>Data Center</label>
            <select className={inputClass} value={dataCenter} onChange={(e) => setDataCenter(e.target.value)}>
              <option value="">Selecciona una opción</option>
              <option value="eu">Europa</option>
              <option value="us">USA</option>
            </select>
          </div>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300">System ID por ambiente</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelClass}>DEV</label>
            <input type="text" className={inputClass} value={systemIds.dev} onChange={(e) => setSystemIds(prev => ({ ...prev, dev: e.target.value }))} placeholder="Ej: DEVCLNT100" />
            <RadioDefaultTest valor="dev" actual={systemIdTestPorDefecto} onChange={setSystemIdTestPorDefecto} />
          </div>
          <div>
            <label className={labelClass}>QAS</label>
            <input type="text" className={inputClass} value={systemIds.qas} onChange={(e) => setSystemIds(prev => ({ ...prev, qas: e.target.value }))} placeholder="Ej: QASCLNT123" />
            <RadioDefaultTest valor="qas" actual={systemIdTestPorDefecto} onChange={setSystemIdTestPorDefecto} />
          </div>
          <div>
            <label className={labelClass}>PRD</label>
            <input type="text" className={inputClass} value={systemIds.prd} onChange={(e) => setSystemIds(prev => ({ ...prev, prd: e.target.value }))} placeholder="Ej: PRDCLNT200" />
          </div>
        </div>
        <p className="text-xs text-gray-500">
          "Default para test" define qué System ID se usa al enviar estatus al ambiente test de Ariba (por ejemplo desde la extensión de Chrome de soporte).
          Si no marcas ninguno, se usa el único que tenga valor; si están los dos, QAS. PRD siempre usa el System ID de PRD.
          {systemIdTestPorDefecto && (
            <button type="button" onClick={() => setSystemIdTestPorDefecto("")} className="ml-2 text-blue-400 hover:underline">Quitar default</button>
          )}
        </p>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <Fingerprint className="w-4 h-4 text-blue-400" /> ANID de Business Network
        </h3>
        <p className="text-xs text-gray-500">
          ANID de la cuenta de comprador de este cliente en SAP Business Network (sin el sufijo -T de test; sirve para test y producción).
          Con él, el soporte de OneHub identifica al cliente y su configuración. Un ANID solo puede pertenecer a un cliente.
        </p>

        {errorAnids && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{errorAnids}</div>
        )}
        {successAnids && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs">{successAnids}</div>
        )}

        {esAdmin ? (
          <>
            <div>
              <label className={labelClass}>ANIDs (uno por línea)</label>
              <textarea
                className={`${inputClass} font-mono h-20 resize-y`}
                value={anidsTexto}
                onChange={(e) => setAnidsTexto(e.target.value)}
                placeholder="AN01404537294"
              />
            </div>
            <div className="flex justify-end">
              <button
                onClick={handleGuardarAnids}
                disabled={guardandoAnids}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-xs font-semibold text-[#fff] transition-all"
              >
                {guardandoAnids ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Guardar ANIDs
              </button>
            </div>
          </>
        ) : (
          <p className="text-xs text-gray-400 bg-black/20 border border-white/10 rounded-lg p-3">
            {buyerAnids.length ? <span className="font-mono text-gray-300">{buyerAnids.join(", ")}</span> : "Sin ANID configurado."}
            <span className="block mt-1 text-gray-500">Solo un administrador de la plataforma puede editarlo.</span>
          </p>
        )}
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-blue-400" /> Código de confirmación para Producción
        </h3>
        <p className="text-xs text-gray-500">
          Se pide al consultor antes de enviar datos al ambiente PRD (ver "Confirmar Producción" en Estatus de Facturas). Recomendado: 6 o más caracteres.
        </p>

        {errorCodigo && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{errorCodigo}</div>
        )}
        {successCodigo && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg text-xs">{successCodigo}</div>
        )}

        {tieneCodigoProduccion && (
          <p className="text-xs text-gray-400 bg-black/20 border border-white/10 rounded-lg p-3">
            Ya hay un código guardado para este cliente. Completa los campos de abajo solo si quieres reemplazarlo.
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Nuevo código</label>
            <input type="password" className={inputClass} value={codigoProduccion} onChange={(e) => setCodigoProduccion(e.target.value)} placeholder="••••••••" />
          </div>
          <div>
            <label className={labelClass}>Confirmar código</label>
            <input type="password" className={inputClass} value={confirmarCodigoProduccion} onChange={(e) => setConfirmarCodigoProduccion(e.target.value)} placeholder="••••••••" />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleGuardarCodigoProduccion}
            disabled={guardandoCodigo}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-xs font-semibold text-[#fff] transition-all"
          >
            {guardandoCodigo ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Guardar código
          </button>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-2xl p-5 space-y-4">
        <h3 className="text-sm font-semibold text-gray-300 flex items-center gap-2">
          <KeyRound className="w-4 h-4 text-blue-400" /> Credenciales del PUser
        </h3>

        {tieneCredencialesGuardadas && (
          <p className="text-xs text-gray-400 bg-black/20 border border-white/10 rounded-lg p-3">
            Ya hay credenciales guardadas para este cliente ({usuarioGuardado}). Completa los campos de abajo solo si quieres reemplazarlas.
          </p>
        )}

        {tieneCredencialesGuardadas && (
          <FlujoAprobacionCredencial credencial={{ tipo: "cig", clienteId }} version={versionCredencial} />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Usuario (PUser)</label>
            <input type="text" className={inputClass} value={usuario} onChange={(e) => setUsuario(e.target.value)} placeholder="Ej: P000000" />
          </div>
          <div>
            <label className={labelClass}>Contraseña</label>
            <input type="password" className={inputClass} value={contraseña} onChange={(e) => setContraseña(e.target.value)} placeholder="••••••••" />
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
          <input
            type="checkbox"
            checked={guardarCredenciales}
            onChange={(e) => setGuardarCredenciales(e.target.checked)}
            className="w-4 h-4 rounded border-white/10 bg-zinc-800 accent-blue-500"
          />
          Guardar estas credenciales para este cliente
        </label>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleGuardar}
          disabled={guardando}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 px-5 py-2.5 rounded-lg text-sm font-semibold text-[#fff] transition-all"
        >
          <Save className="w-4 h-4" />
          {guardando ? "Guardando..." : "Guardar configuración"}
        </button>
      </div>
    </div>
  );
}

function RadioDefaultTest({ valor, actual, onChange }: { valor: "dev" | "qas"; actual: SystemIdTestPorDefecto; onChange: (v: SystemIdTestPorDefecto) => void }) {
  return (
    <label className="mt-2 flex items-center gap-2 text-xs text-gray-400 cursor-pointer">
      <input
        type="radio"
        name="systemIdTestPorDefecto"
        checked={actual === valor}
        onChange={() => onChange(valor)}
        className="accent-blue-500"
      />
      Default para test
    </label>
  );
}
